import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Image as ImageIcon, Video, Youtube, ExternalLink, ZoomIn, Facebook } from 'lucide-react';
import { API } from '../App';
import ImageLightbox from '../components/ImageLightbox';

function Gallery() {
  const [media, setMedia] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sermons');
  const [latestVideo, setLatestVideo] = useState(null);
  const [videoLoading, setVideoLoading] = useState(true);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    // Fetch both in parallel
    const fetchAllData = async () => {
      try {
        const [mediaRes, videoRes] = await Promise.all([
          axios.get(`${API}/media`),
          axios.get(`${API}/youtube/latest`).catch(() => ({
            data: {
              video_id: 'AwF2trYh0ig',
              title: 'Latest Sermon',
              description: 'Watch our latest sermon',
              published_at: null
            }
          }))
        ]);
        setMedia(mediaRes.data);
        setLatestVideo(videoRes.data);
      } catch (error) {
        toast.error('Failed to load media');
      } finally {
        setLoading(false);
        setVideoLoading(false);
      }
    };
    fetchAllData();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/media`);
      setMedia(response.data);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestVideo = async () => {
    try {
      const response = await axios.get(`${API}/youtube/latest`);
      setLatestVideo(response.data);
    } catch (error) {
      console.error('Failed to load latest video:', error);
      // Fallback to default video
      setLatestVideo({
        video_id: 'AwF2trYh0ig',
        title: 'Latest Sermon',
        description: 'Watch our latest sermon',
        published_at: null
      });
    } finally {
      setVideoLoading(false);
    }
  };

  const filteredMedia = filter === 'all' ? media : media.filter(m => m.type === filter);

  // Get only images for lightbox navigation
  const imageMedia = filteredMedia.filter(m => m.type === 'image');

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleLightboxNavigate = (direction) => {
    if (direction === 'prev') {
      setLightboxIndex((prev) => (prev === 0 ? imageMedia.length - 1 : prev - 1));
    } else {
      setLightboxIndex((prev) => (prev === imageMedia.length - 1 ? 0 : prev + 1));
    }
  };

  // Format date for display
  const formatVideoDate = (dateString) => {
    if (!dateString) return 'Recent';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-2xl" style={{ color: '#1e3a8a' }}>Loading media...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <div className="section-padding bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="media-title">
            Media
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Watch our sermons and view photos from our church community
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-4 justify-center">
          <button
            onClick={() => setActiveTab('sermons')}
            className={`px-8 py-4 rounded-full text-lg font-semibold ${activeTab === 'sermons' ? 'btn-primary' : 'btn-secondary'}`}
            data-testid="tab-sermons"
          >
            <Youtube size={24} className="inline mr-2" />
            Sermon Videos
          </button>
          <button
            onClick={() => setActiveTab('photos')}
            className={`px-8 py-4 rounded-full text-lg font-semibold ${activeTab === 'photos' ? 'btn-primary' : 'btn-secondary'}`}
            data-testid="tab-photos"
          >
            <ImageIcon size={24} className="inline mr-2" />
            Photo Gallery
          </button>
          <button
            onClick={() => setActiveTab('facebook')}
            className={`px-8 py-4 rounded-full text-lg font-semibold ${activeTab === 'facebook' ? 'btn-primary' : 'btn-secondary'}`}
            data-testid="tab-facebook"
          >
            <Facebook size={24} className="inline mr-2" />
            Facebook
          </button>
        </div>
      </div>

      {/* Sermon Videos Section */}
      {activeTab === 'sermons' && (
        <div className="section-padding bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* YouTube Channel Link */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-8 mb-12 text-white text-center">
              <Youtube size={48} className="mx-auto mb-4" />
              <h2 className="text-3xl font-bold mb-4">Watch More on YouTube</h2>
              <p className="text-xl mb-6 max-w-2xl mx-auto">
                Subscribe to our YouTube channel to watch all our sermons, Bible studies, and special events.
              </p>
              <a 
                href="https://www.youtube.com/@CORNERSTONENazareneChurch" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-red-600 px-8 py-4 rounded-full text-xl font-bold hover:bg-gray-100 transition"
                data-testid="youtube-channel-link"
              >
                <Youtube size={24} />
                Visit Our Channel
                <ExternalLink size={20} />
              </a>
            </div>

            {/* Sermon Grid */}
            <h2 className="text-4xl font-bold text-center mb-8" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}>
              Latest Sermon
            </h2>
            
            {/* Featured Sermon - Dynamically loaded */}
            <div className="max-w-4xl mx-auto mb-12">
              {videoLoading ? (
                <div className="card p-12 text-center">
                  <div className="animate-pulse">
                    <div className="bg-slate-200 rounded-lg" style={{ aspectRatio: '16/9' }}></div>
                    <div className="h-6 bg-slate-200 rounded mt-6 w-3/4 mx-auto"></div>
                    <div className="h-4 bg-slate-200 rounded mt-2 w-1/2 mx-auto"></div>
                  </div>
                </div>
              ) : latestVideo ? (
                <div className="card overflow-hidden" data-testid="latest-sermon-card">
                  <div className="bg-slate-900 rounded-t-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                    <iframe
                      src={`https://www.youtube.com/embed/${latestVideo.video_id}`}
                      title={latestVideo.title}
                      className="w-full h-full"
                      allowFullScreen
                      data-testid="latest-sermon-iframe"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-bold" style={{ color: '#1e3a8a' }} data-testid="latest-sermon-title">
                      {latestVideo.title}
                    </h3>
                    <p className="text-lg text-slate-600" data-testid="latest-sermon-date">
                      {formatVideoDate(latestVideo.published_at)}
                    </p>
                    {latestVideo.description && (
                      <p className="text-slate-500 mt-2">{latestVideo.description}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="card p-8 text-center">
                  <p className="text-slate-600">Unable to load latest video. Please visit our YouTube channel.</p>
                </div>
              )}
            </div>

            {/* Channel Videos Embed */}
            <div className="bg-slate-100 rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Watch All Our Sermons</h3>
              <p className="text-lg text-slate-700 mb-6">
                Visit our YouTube channel to browse all past sermons, Bible studies, and special events.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="https://www.youtube.com/@CORNERSTONENazareneChurch/videos" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                >
                  <Youtube size={24} />
                  Browse All Videos
                </a>
                <a 
                  href="https://www.youtube.com/@CORNERSTONENazareneChurch?sub_confirmation=1" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                >
                  Subscribe to Channel
                </a>
              </div>
            </div>

            {/* More Videos Link */}
            <div className="text-center mt-12">
              <a 
                href="https://www.youtube.com/@CORNERSTONENazareneChurch/videos" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-secondary inline-flex items-center gap-2 text-xl"
                data-testid="view-all-sermons"
              >
                View All Sermons on YouTube
                <ExternalLink size={20} />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Photo Gallery Section */}
      {activeTab === 'photos' && (
        <div className="section-padding bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-4 justify-center mb-8">
              <button
                onClick={() => setFilter('all')}
                className={`px-6 py-3 rounded-full text-lg font-semibold ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
                data-testid="filter-all"
              >
                All Media
              </button>
              <button
                onClick={() => setFilter('image')}
                className={`px-6 py-3 rounded-full text-lg font-semibold ${filter === 'image' ? 'btn-primary' : 'btn-secondary'}`}
                data-testid="filter-images"
              >
                <ImageIcon size={20} className="inline mr-2" />
                Photos
              </button>
              <button
                onClick={() => setFilter('video')}
                className={`px-6 py-3 rounded-full text-lg font-semibold ${filter === 'video' ? 'btn-primary' : 'btn-secondary'}`}
                data-testid="filter-videos"
              >
                <Video size={20} className="inline mr-2" />
                Videos
              </button>
            </div>

            {/* Gallery Grid */}
            {filteredMedia.length === 0 ? (
              <p className="text-xl text-center text-slate-600">No media items found.</p>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredMedia.map((item, index) => {
                  // Calculate the index within imageMedia for lightbox
                  const imageIndex = item.type === 'image' ? imageMedia.findIndex(img => img.id === item.id) : -1;
                  
                  return (
                    <div key={item.id} className="card overflow-hidden" data-testid={`media-${item.id}`}>
                      {item.type === 'image' ? (
                        <div 
                          className="relative group cursor-pointer"
                          onClick={() => openLightbox(imageIndex)}
                          data-testid={`gallery-image-${item.id}`}
                        >
                          <img src={item.url} alt={item.title || 'Church photo'} className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105" />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white rounded-full p-3 shadow-lg">
                              <ZoomIn size={28} className="text-blue-900" />
                            </div>
                          </div>
                          <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <span className="text-white text-sm bg-black bg-opacity-60 px-3 py-1 rounded-full">
                              Click to enlarge
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="relative w-full h-64 bg-slate-900">
                          <iframe
                            src={item.url}
                            title={item.title || 'Church video'}
                            className="w-full h-full"
                            allowFullScreen
                          />
                        </div>
                      )}
                      {(item.title || item.description) && (
                        <div className="p-4">
                          {item.title && (
                            <h3 className="text-xl font-bold mb-2" style={{ color: '#1e3a8a' }}>{item.title}</h3>
                          )}
                          {item.description && (
                            <p className="text-lg text-slate-600">{item.description}</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Image Lightbox */}
            <ImageLightbox
              isOpen={lightboxOpen}
              onClose={() => setLightboxOpen(false)}
              images={imageMedia.map(img => ({ url: img.url, alt: img.title || 'Church photo' }))}
              currentIndex={lightboxIndex}
              onNavigate={handleLightboxNavigate}
            />
          </div>
        </div>
      )}

      {/* Facebook Section */}
      {activeTab === 'facebook' && (
        <div className="section-padding bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Facebook Page Link */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-8 mb-12 text-white text-center">
              <Facebook size={64} className="mx-auto mb-4" />
              <h2 className="text-3xl font-bold mb-4">Follow Us on Facebook</h2>
              <p className="text-xl mb-6 max-w-2xl mx-auto">
                Stay connected with our church community! Follow our Facebook page for updates, photos, event announcements, and inspirational messages.
              </p>
              <a 
                href="https://www.facebook.com/coosbaycornerstone.nazarene/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-full text-xl font-bold hover:bg-gray-100 transition"
              >
                <Facebook size={28} />
                Visit Our Facebook Page
                <ExternalLink size={20} />
              </a>
            </div>

            {/* Facebook Page Embed */}
            <div className="card p-8">
              <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: '#1e3a8a' }}>
                Latest from Our Facebook
              </h3>
              <div className="flex justify-center">
                <div className="relative">
                  <iframe 
                    src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2Fcoosbaycornerstone.nazarene%2F&tabs=timeline&width=500&height=600&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true"
                    width="500" 
                    height="600" 
                    style={{ border: 'none', overflow: 'hidden', maxWidth: '100%' }}
                    scrolling="no" 
                    frameBorder="0" 
                    allowFullScreen={true}
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                    title="Coos Bay Cornerstone Facebook Page"
                    className="rounded-lg shadow-lg"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  {/* Fallback content shown below iframe - visible if iframe fails to load */}
                  <noscript>
                    <div className="text-center p-8">
                      <p className="text-slate-600 mb-4">Facebook feed requires JavaScript to display.</p>
                    </div>
                  </noscript>
                </div>
              </div>
              
              {/* Alternative: Direct Facebook link card */}
              <div className="mt-8 bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-6 text-white text-center">
                <h4 className="text-xl font-bold mb-3">Can't see the feed above?</h4>
                <p className="mb-4 opacity-90">
                  Facebook's embed may not load in all browsers. Visit our page directly to see our latest posts!
                </p>
                <a 
                  href="https://www.facebook.com/coosbaycornerstone.nazarene/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-blue-800 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                  data-testid="facebook-fallback-link"
                >
                  <Facebook size={24} />
                  Open Facebook Page
                  <ExternalLink size={18} />
                </a>
              </div>
              
              <p className="text-center text-slate-500 mt-6">
                Like and follow our page to see our latest posts and updates!
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Gallery;
